const hasAtomicDescendant = (node) => {
  if (!node.childNodes) return false;
  for (let child of node.childNodes) {
    if (
      child.nodeType === Node.ELEMENT_NODE &&
      child.dataset.atomic === 'true'
    ) {
      return true;
    }
    if (hasAtomicDescendant(child)) return true;
  }
  return false;
};

const isEmptyEditorElement = (node) => {
  const text = (node.textContent || '').replace(/[\u200B\u00A0]/g, '').trim();
  if (text) return false;

  return !node.querySelector('img, video, audio, iframe, canvas, svg');
};

const removeColorStylesRecursive = (node) => {
  if (
    node.nodeType === Node.ELEMENT_NODE
  ) {
    if (node.hasAttribute('style')) {
      const filteredStyles = node
        .getAttribute('style')
        .split(';')
        .map(part => part.trim())
        .filter(part => {
          if (!part) return false;
          const lower = part.toLowerCase();
          return !lower.startsWith('color:') && !lower.startsWith('font-family:');
        });

      if (filteredStyles.length) {
        node.setAttribute('style', filteredStyles.join('; '));
      } else {
        node.removeAttribute('style');
      }
    }
    if (node.hasAttribute('color')) {
      node.removeAttribute('color');
    }
  }

  Array.from(node.childNodes || []).forEach(child => removeColorStylesRecursive(child));
};

const sanitizePostHtml = (html) => {
  if (!html || typeof document === 'undefined') return html;
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll('style').forEach(style => style.remove());
  template.content.querySelectorAll('font').forEach(font => {
    const fragment = document.createDocumentFragment();
    Array.from(font.childNodes).forEach(child => fragment.appendChild(child));
    font.replaceWith(fragment);
  });
  Array.from(template.content.childNodes || []).forEach(child => removeColorStylesRecursive(child));
  return template.innerHTML;
};

export const getPayload = (editableRef, formState) => {
  // Build post_structure + post_texts from editableRef DOM...
  const post_structure = [];
  const post_texts = [];
  let textId = 1;
  const container = editableRef?.current;

  const walkDOMNodes = (node) => {
    if (
      node.nodeType === Node.ELEMENT_NODE &&
      node.dataset?.placeholderBlock === 'true' &&
      !node.textContent.trim()
    ) {
      // Skip artificial spacer nodes unless the user added content.
      return;
    }
    if (
      node.nodeType === Node.ELEMENT_NODE &&
        node.dataset.atomic === 'true'
    ) {
      const [kind, idx] = node.id.split('-');
      const idNum = Number(idx);
      const typeMap = {
        poll: 'post_polls',
        quiz: 'post_quizzes',
        groupPicture: 'post_pictures',
        groupArtwork: 'post_artworks',
      };
      if (!isNaN(idNum) && typeMap[kind]) {
        post_structure.push({ type: typeMap[kind], id: idNum });
      }
      return;
    }

    if (node.nodeType === Node.TEXT_NODE) {
      const content = node.textContent.trim();
      if (!content) return;
      post_texts.push({ textId, content });
      post_structure.push({ type: 'post_texts', id: textId });
      textId += 1;
      return;
    }

    // For non-atomic elements
    if (
      node.nodeType === Node.ELEMENT_NODE &&
      !hasAtomicDescendant(node)
    ) {
      if (isEmptyEditorElement(node)) return;

      // Only add if no atomic descendants
      post_texts.push({ textId, content: sanitizePostHtml(node.outerHTML) });
      post_structure.push({ type: 'post_texts', id: textId });
      textId += 1;
      return;
    }

    node.childNodes.forEach(child => walkDOMNodes(child));
  };

  if(container?.childNodes) {
    Array.from(container.childNodes).forEach(node => walkDOMNodes(node));
  }

  const mediaElementTypes = ['post_pictures', 'post_artworks'];
  const structureKey = block => `${block.type}-${block.id}`;
  const existingStructureKeys = new Set(post_structure.map(block => structureKey(block)));

  const formStructure = formState?.post_structure || [];
  const existingMediaStructure = formStructure.filter(block => mediaElementTypes.includes(block.type));
  const availableMediaGroups = new Set([
    ...(formState.post_pictures || []).map(group => `post_pictures-${group.groupId}`),
    ...(formState.post_artworks || []).map(group => `post_artworks-${group.groupId}`),
  ]);

  const missingMediaStructure = existingMediaStructure.filter(block =>
    availableMediaGroups.has(structureKey(block)) &&
    !existingStructureKeys.has(structureKey(block))
  );

  const finalPostStructure = [...missingMediaStructure, ...post_structure];

  const payload = {
    ...formState,
    post_structure: finalPostStructure,
    post_texts,
  };

  return payload;
};

export { sanitizePostHtml };
